import axios from "@/plugins/axios";

// Flag para evitar registrar listeners múltiples veces
let listenersRegistered = false;

// Importaciones dinámicas de Capacitor (solo disponibles en modo nativo)
let PushNotifications, FCM, Device, Badge;

async function loadCapacitorModules() {
  if (typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform()) {
    try {
      ({ PushNotifications } = await import("@capacitor/push-notifications"));
      ({ FCM } = await import("@capacitor-community/fcm"));
      ({ Device } = await import("@capacitor/device"));
      ({ Badge } = await import("@capawesome/capacitor-badge"));
      return true;
    } catch (error) {
      return false;
    }
  }
  return false;
}

export async function initPush() {
  const loaded = await loadCapacitorModules();
  if (!loaded || !PushNotifications) return;

  const perm = await PushNotifications.requestPermissions();

  if (perm.receive !== "granted") return;

  // Registrar listeners solo una vez
  if (!listenersRegistered) {
    PushNotifications.addListener("registration", async (token) => {
      try {
        const fcm = await FCM.getToken();
        await saveFcmToken(fcm.token);
      } catch (e) {
        // Error getting FCM token
      }
    });

    PushNotifications.addListener("registrationError", (error) => {
      // Error registering for push notifications
    });

    // Listener cuando llega una notificación con la app abierta
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      // Disparar evento personalizado para que la app recargue los datos
      window.dispatchEvent(new CustomEvent('reloadAppData', { detail: notification }));
    });

    // Listener cuando el usuario toca una notificación
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      // Disparar evento personalizado para que la app recargue los datos
      window.dispatchEvent(new CustomEvent('reloadAppData', { detail: action.notification }));
    });

    listenersRegistered = true;
  }

  // Esto genera APNS token
  await PushNotifications.register();

  // IMPORTANTE: Siempre intentar obtener el token actual y enviarlo al servidor
  // Esto es crucial cuando se reinstala la app y el token ha cambiado
  try {
    const fcm = await FCM.getToken();
    await saveFcmToken(fcm.token);
  } catch (e) {
    // Error getting initial FCM token
  }
}

export async function shouldAskForNotifications() {
  const loaded = await loadCapacitorModules();
  if (!loaded || !PushNotifications) return "not_available";

  const status = await PushNotifications.checkPermissions();
  if (status.receive === "granted") return "granted";
  if (status.receive === "denied") return "denied";
  return "ask";
}

/*
   Se usa cuando la app vuelve a primer plano para actualizar el token si es necesario
*/
export async function reRegisterPushIfNeeded() {
  const loaded = await loadCapacitorModules();
  if (!loaded || !PushNotifications) return;

  const status = await PushNotifications.checkPermissions();
  const last = localStorage.getItem("notifications_status");

  // Si tiene permisos granted, asegurarse de que el token esté actualizado
  if (status.receive === "granted") {
    // Si antes no era granted, hacer registro completo
    if (last !== "granted") {
      localStorage.setItem("notifications_status", "granted");
      await initPush();
    } else {
      // Si ya era granted, solo actualizar el token por si cambió
      try {
        const fcm = await FCM.getToken();
        await saveFcmToken(fcm.token);
      } catch (e) {
        // Error updating FCM token
      }
    }
  } else {
    localStorage.setItem("notifications_status", status.receive);
  }
}

// guardar token
async function saveFcmToken(token) {
  try {
    if (!Device) return;

    // Obtener información del dispositivo
    const deviceInfo = await Device.getInfo();
    const deviceId = await Device.getId();

    await axios.post("/notifications/token", {
      token,
      device_type: deviceInfo.platform, // "ios" o "android"
      device_id: deviceId.identifier // UUID del dispositivo
    });
  } catch (e) {
    // Error saving FCM token
  }
}

// Limpiar todas las notificaciones entregadas y el badge
export async function clearAllNotifications() {
  const loaded = await loadCapacitorModules();
  if (!loaded || !PushNotifications) return;

  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (e) {
    // Error clearing notifications
  }
}

// Obtener el número de notificaciones entregadas
export async function getDeliveredNotifications() {
  const loaded = await loadCapacitorModules();
  if (!loaded || !PushNotifications) return [];

  try {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  } catch (e) {
    return [];
  }
}

// Establecer el badge con un número específico
export async function setBadge(count) {
  const loaded = await loadCapacitorModules();
  if (!loaded || !Badge) return;

  try {
    if (count > 0) {
      await Badge.set({ count });
    } else {
      await Badge.clear();
    }
  } catch (e) {
    // Error setting badge
  }
}

// Limpiar el badge (establecer a 0)
export async function clearBadge() {
  const loaded = await loadCapacitorModules();
  if (!loaded || !Badge) return;

  try {
    await Badge.clear();
  } catch (e) {
    // Error clearing badge
  }
}

// Obtener el valor actual del badge
export async function getBadge() {
  const loaded = await loadCapacitorModules();
  if (!loaded || !Badge) return 0;

  try {
    const result = await Badge.get();
    return result.count || 0;
  } catch (e) {
    return 0;
  }
}